import { ActionIcon, Tooltip } from "@mantine/core";
import {
  Camera,
  Copy,
  Flag,
  Pause,
  Play,
  Scissors,
  SkipBack,
  SkipForward,
  Trash2,
  Minus,
  PanelBottomClose,
  PanelBottomOpen,
  Plus,
} from "lucide-react";
import type { ReactNode } from "react";

import { formatTimecode, formatTotalTimecode } from "./time";
import classes from "./editor.module.css";

/** Live's zoom: 115% at first, 15 points a step, from 10% to 1000% (a share of `PX_PER_SECOND`). */
export const DEFAULT_ZOOM = 1.15;
const ZOOM_STEP = 0.15;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 10;

/** `zoom` moved by `steps` of live's step, kept in live's range, without float drift. */
export function zoomBy(zoom: number, steps: number): number {
  const next = Math.round((zoom + steps * ZOOM_STEP) * 100) / 100;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
}

export interface TimelineToolbarProps {
  playing: boolean;
  /** False without a clip: nothing to play. */
  canPlay: boolean;
  onPlay: () => void;
  onSeek: (ms: number) => void;
  playheadMs: number;
  totalMs: number;
  /** Removes the selected clip or title; `undefined` when nothing removable is selected. */
  onDelete: (() => void) | undefined;
  /** Cuts the selected clip at the playhead; `undefined` unless the playhead is inside it. */
  onSplit: (() => void) | undefined;
  /** A copy of the selected clip on a new track above; `undefined` without one. */
  onDuplicate: (() => void) | undefined;
  /** Live's Snapshot: the frame at the playhead saved as a picture. */
  onSnapshot: () => void;
  /** A marker at the playhead (spec markers); `undefined` for a viewer. */
  onAddMarker: (() => void) | undefined;
  zoom: number;
  onZoom: (zoom: number) => void;
  /** Live's folded timeline (decision 21): the clip tools and the zoom are off. */
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
  /** The timeline's switches at the right end, before the zoom (spec capcut-shortcuts.md). */
  switches: readonly ToolbarSwitch[];
}

/** A switch of the toolbar's right end: Snapping, Preview axis, and later the magnet. */
export interface ToolbarSwitch {
  label: string;
  icon: ReactNode;
  on: boolean;
  onToggle: () => void;
}

function Tool(props: { label: string; onClick?: (() => void) | undefined; children: ReactNode }) {
  return (
    <Tooltip label={props.label}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={32}
        aria-label={props.label}
        disabled={props.onClick === undefined}
        onClick={props.onClick}
      >
        {props.children}
      </ActionIcon>
    </Tooltip>
  );
}

/** A switch, lit while on; folded, off-limits as the zoom is. */
function SwitchTool(props: { item: ToolbarSwitch; folded: boolean }) {
  const { item, folded } = props;
  return (
    <Tooltip label={item.label}>
      <ActionIcon
        variant="subtle"
        color="gray"
        size={32}
        aria-label={item.label}
        aria-pressed={item.on}
        className={classes.switchTool}
        data-on={item.on}
        disabled={folded}
        onClick={item.onToggle}
      >
        {item.icon}
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The timeline's toolbar, in live's three parts: the edit tools on the left (delete, split at the
 * playhead, duplicate and snapshot), the transport and the clock in the middle, the switches, the
 * zoom and the collapse on the right.
 */
export function TimelineToolbar(props: TimelineToolbarProps) {
  const { playing, canPlay, onPlay, onSeek, playheadMs, totalMs, zoom, onZoom, collapsed, onCollapse } = props;
  // Folded, live's clip tools, snapshot and zoom are off (`simplified`).
  const onDelete = collapsed ? undefined : props.onDelete;
  const onSplit = collapsed ? undefined : props.onSplit;
  const onDuplicate = collapsed ? undefined : props.onDuplicate;
  const onSnapshot = collapsed ? undefined : props.onSnapshot;
  const onAddMarker = collapsed ? undefined : props.onAddMarker;
  return (
    <div className={classes.toolbar} role="toolbar" aria-label="Timeline tools">
      <div className={classes.toolGroup}>
        <Tool label="Delete" onClick={onDelete}>
          <Trash2 size={15} />
        </Tool>
        <Tool label="Split" onClick={onSplit}>
          <Scissors size={15} />
        </Tool>
        <Tool label="Duplicate" onClick={onDuplicate}>
          <Copy size={15} />
        </Tool>
        {/* Live's Snapshot asks for no selection: the frame at the playhead, whatever it shows. */}
        <Tool label="Snapshot" onClick={onSnapshot}>
          <Camera size={15} />
        </Tool>
        <Tool label="Add marker" onClick={onAddMarker}>
          <Flag size={15} />
        </Tool>
      </div>
      <div className={classes.toolGroup}>
        <Tool label="Go to start" onClick={canPlay ? () => onSeek(0) : undefined}>
          <SkipBack size={15} />
        </Tool>
        <Tool label={playing ? "Pause" : "Play"} onClick={canPlay ? onPlay : undefined}>
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </Tool>
        <Tool label="Go to end" onClick={canPlay ? () => onSeek(totalMs) : undefined}>
          <SkipForward size={15} />
        </Tool>
        <span className={classes.clock}>
          <span className={classes.clockNow}>{formatTimecode(playheadMs, true)}</span>
          <span aria-hidden>|</span>
          <span className={classes.clockTotal}>{formatTotalTimecode(totalMs)}</span>
        </span>
      </div>
      <div className={classes.toolGroupEnd}>
        {props.switches.map((item) => (
          <SwitchTool key={item.label} item={item} folded={collapsed} />
        ))}
        {props.switches.length === 0 ? null : <span className={classes.toolDivider} aria-hidden />}
        <Tool label="Zoom out" onClick={!collapsed && zoom > MIN_ZOOM ? () => onZoom(zoomBy(zoom, -1)) : undefined}>
          <Minus size={15} />
        </Tool>
        <span className={classes.zoom}>
          {Math.round(zoom * 100)}%
        </span>
        <Tool
          label="Zoom in"
          onClick={!collapsed && zoom < MAX_ZOOM ? () => onZoom(zoomBy(zoom, 1)) : undefined}
        >
          <Plus size={15} />
        </Tool>
        <span className={classes.toolDivider} aria-hidden />
        {/* Live's titles for its panel toggle. */}
        <Tool
          label={collapsed ? "Expand Timeline" : "Collapse Timeline"}
          onClick={() => onCollapse(!collapsed)}
        >
          {collapsed ? <PanelBottomOpen size={15} /> : <PanelBottomClose size={15} />}
        </Tool>
      </div>
    </div>
  );
}
